import React, { useEffect, useState } from 'react';
import { CreditApplicationTemplate } from '@dill/dill-shared';
import { X } from 'react-feather';
import { Plus } from 'phosphor-react';
import { DillButton, DillModal } from '../../../general/Dill';
import { DillInput } from '../../../general/Dill/DillInput';
import { DillDropdown } from '../../../general/Dill/DillDropdown';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppSelector } from '../../../../redux/store';
import EclipseFormApprovalFields from './EclipseFormApprovalFields';
import { getIntegrationName } from '../../../../utils/helpers';
import { getApprovalFieldOptions } from '../CreditApplicationComponents/approvalFieldOptions';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type InternalFormCustomerAction = 'update' | 'create' | 'none';

export type InternalFormApproval = {
  customerAction: InternalFormCustomerAction;
  billToCustomerId: string;
  shipToAccountNumber: string;
  notifyEmails: string[];
  message: string;
  // With customerAction 'create': create the P21 customer as a COD account.
  createAsCod: boolean;
  // Only when the form requests a credit limit: the amount pushed to P21.
  approvedCreditLimit?: number;
  // Answers to the template's approvalAdditionalFields.
  approvalAdditionalFieldValues: ApprovalFieldValue[];
  // Eclipse, creating a Ship-To: its terms (a term name from the supplier's list).
  terms?: string;
};

export type ApprovalField = NonNullable<
  CreditApplicationTemplate['approvalAdditionalFields']
>[number];

type ApprovalFieldValue = { id: string; name: string; erpId?: string; value?: string };

const CUSTOM_OPTION = '__custom__';

// "25000.5" -> "25,000.5", keeping a trailing "." while it's being typed.
const withThousandsCommas = (value: string) => {
  const [whole, decimals] = value.split('.');
  return (
    (whole ? Number(whole).toLocaleString('en-US') : '') +
    (decimals !== undefined ? `.${decimals}` : '')
  );
};

type ApprovalFieldOption = NonNullable<ApprovalField['options']>[number];

const optionName = (option: ApprovalFieldOption) =>
  typeof option === 'object' ? option.name : option;

// The template's approvalAdditionalFields (e.g. Branch, Shipping all at once?, Inside / Outside
// Sales Associate, Estimated Job $ Amount), keyed by field id. Same field types as the credit
// application approval modal: text, number, currency and dropdown (optionally with "Other").
const ApprovalAdditionalFieldInputs = ({
  fields,
  values,
  customValues,
  errors,
  onChange,
  onCustomChange,
}: {
  fields: ApprovalField[];
  values: Record<string, { value: string; erpId?: string }>;
  customValues: Record<string, string>;
  errors: Record<string, string>;
  onChange: (fieldId: string, value: string, erpId?: string) => void;
  onCustomChange: (fieldId: string, value: string) => void;
}) => {
  // For dropdowns whose options are the supplier's branches.
  const supplierBranches = useAppSelector((state) => state.supplierBranches.branches);
  return (
    <div className="flex flex-col gap-3">
      <p className="text-xs font-medium text-gray-500">Approval details</p>
      {fields.map((field) => {
        const value = values[field.id]?.value ?? '';
        if (field.type === 'dropdown') {
          const options = getApprovalFieldOptions(field, supplierBranches);
          return (
            <div key={field.id} className="flex flex-col gap-1">
              <DillDropdown
                containerClassName="h-[40px] p-1"
                label={field.label}
                isRequired={field.required}
                placeholder="Select..."
                value={value || null}
                errorText={errors[field.id]}
                items={[
                  ...options.map((option) => ({
                    value: optionName(option),
                    label: optionName(option),
                  })),
                  ...(field.allowCustomOption ? [{ value: CUSTOM_OPTION, label: 'Other' }] : []),
                ]}
                onSelectChange={(item) => {
                  const selected = options.find((option) => optionName(option) === item.value);
                  onChange(
                    field.id,
                    item.value,
                    typeof selected === 'object' && selected !== null ? selected.erpId : undefined
                  );
                }}
              />
              {field.allowCustomOption && value === CUSTOM_OPTION && (
                <AppInputField
                  label=""
                  placeholder="Specify..."
                  value={customValues[field.id] ?? ''}
                  onTextChange={(text: string) => onCustomChange(field.id, text)}
                />
              )}
            </div>
          );
        }
        const isCurrency = field.type === 'currency';
        return (
          <AppInputField
            key={field.id}
            label={field.label}
            isRequired={field.required}
            prefix={isCurrency ? '$' : undefined}
            value={isCurrency ? withThousandsCommas(value) : value}
            errorText={errors[field.id] || ''}
            onTextChange={(text: string) =>
              onChange(
                field.id,
                isCurrency || field.type === 'number'
                  ? `${text}`.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
                  : text
              )
            }
          />
        );
      })}
    </div>
  );
};

export const dedupe = (emails: string[]) =>
  Array.from(new Set(emails.map((email) => email.trim().toLowerCase()).filter(Boolean)));

// Same look as the credit application approval modal's email lists.
export const EmailTagList = ({
  label,
  emails,
  onChange,
}: {
  label: string;
  emails: string[];
  onChange: (emails: string[]) => void;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const addEmail = () => {
    if (!EMAIL_PATTERN.test(inputValue.trim())) {
      setInputError('Invalid email');
      return;
    }
    onChange(dedupe([...emails, inputValue]));
    setInputValue('');
    setInputError('');
  };
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2">
        {emails.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {emails.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1 text-xs bg-white border border-blue-200 text-blue-700 rounded-md px-2 py-1">
                {email}
                <button
                  className="text-blue-400 hover:text-red-500 cursor-pointer bg-transparent border-none p-0 leading-none"
                  onClick={() => onChange(emails.filter((item) => item !== email))}>
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-center">
          <div className="flex-1">
            <DillInput
              value={inputValue}
              type="email"
              placeholder="Add email address"
              error={inputError}
              onChange={(text) => {
                setInputError('');
                setInputValue(text);
              }}
            />
            {inputError && <p className="text-2xs mt-0.5 text-red-500">{inputError}</p>}
          </div>
          <DillButton variant="icon-blue" icon={<Plus size={16} />} onClick={addEmail} />
        </div>
      </div>
    </div>
  );
};

// Opened by Approve on InternalFormSubmissionDetails. The credit team confirms (or corrects) the
// P21 numbers the branch manager entered before anything is pushed to P21 — the push uses these,
// not the form's own answers — or approves without touching P21, and picks who's emailed.
const ApproveInternalFormModal = ({
  open,
  handleClose,
  onConfirm,
  isApproving,
  defaultBillToCustomerId,
  defaultShipToAccountNumber,
  defaultNotifyEmails,
  defaultCustomerAction = 'update',
  defaultCreateAsCod = false,
  requestedCreditLimit,
  isShipToRequired,
  isErpUpdateDisabled = false,
  billToAction = null,
  shipToAction = null,
  approvalFields = [],
}: {
  open: boolean;
  handleClose: () => void;
  onConfirm: (approval: InternalFormApproval) => void;
  isApproving: boolean;
  defaultBillToCustomerId: string;
  defaultShipToAccountNumber: string;
  // The sender and everyone who signed.
  defaultNotifyEmails: string[];
  defaultCustomerAction?: InternalFormCustomerAction;
  // Ticked by default for cash-account forms.
  defaultCreateAsCod?: boolean;
  // The form's requested credit limit (e.g. amount-of-credit-requested); shows Amount Requested /
  // Amount Approved, the latter pre-filled with it.
  requestedCreditLimit?: number;
  // The form changes an existing Ship-To, so P21 needs to know which one.
  isShipToRequired: boolean;
  // The template never updates P21: approve as 'none' with no choice offered.
  isErpUpdateDisabled?: boolean;
  // What approving does to each account when the template fixes it (Eclipse); the dialog then
  // offers only defaultCustomerAction or "Don't update", and asks for the numbers these need.
  billToAction?: 'UPDATE' | 'NONE' | null;
  shipToAction?: 'UPDATE' | 'CREATE' | 'NONE' | null;
  // The template's approvalAdditionalFields, asked for before approving.
  approvalFields?: ApprovalField[];
}) => {
  const [customerAction, setCustomerAction] = useState<InternalFormCustomerAction>('update');
  const [billToCustomerId, setBillToCustomerId] = useState('');
  const [shipToAccountNumber, setShipToAccountNumber] = useState('');
  const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [createAsCod, setCreateAsCod] = useState(false);
  const [approvedAmountText, setApprovedAmountText] = useState('');
  const [errors, setErrors] = useState<{ billTo?: string; shipTo?: string; amount?: string }>({});
  const [approvalFieldValues, setApprovalFieldValues] = useState<
    Record<string, { value: string; erpId?: string }>
  >({});
  const [approvalFieldCustomValues, setApprovalFieldCustomValues] = useState<
    Record<string, string>
  >({});
  const [approvalFieldErrors, setApprovalFieldErrors] = useState<Record<string, string>>({});
  const hasRequestedAmount = typeof requestedCreditLimit === 'number';
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const integrationName = getIntegrationName(currentSupplier?.integrationType);
  // Eclipse suppliers get the Eclipse inputs (EclipseFormApprovalFields) instead of P21's.
  const isEclipse = currentSupplier?.integrationType === 'ECLIPSE';
  const [terms, setTerms] = useState('');

  useEffect(() => {
    if (!open) return;
    setCustomerAction(isErpUpdateDisabled ? 'none' : defaultCustomerAction);
    setBillToCustomerId(defaultBillToCustomerId);
    setShipToAccountNumber(defaultShipToAccountNumber);
    setNotifyEmails(dedupe(defaultNotifyEmails));
    setMessage('');
    setCreateAsCod(defaultCreateAsCod);
    setApprovedAmountText(
      typeof requestedCreditLimit === 'number' ? requestedCreditLimit.toLocaleString('en-US') : ''
    );
    setErrors({});
    // Seeded with each field's default, like the credit application approval modal.
    setApprovalFieldValues(
      Object.fromEntries(
        approvalFields
          .filter((field) => field.defaultValue !== undefined && `${field.defaultValue}` !== '')
          .map((field) => [field.id, { value: `${field.defaultValue}`, erpId: field.erpId }])
      )
    );
    setApprovalFieldCustomValues({});
    setApprovalFieldErrors({});
    setTerms('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultBillToCustomerId, defaultShipToAccountNumber]);

  // "Other" on a dropdown sends what was typed in.
  const resolveApprovalFieldValue = (fieldId: string) => {
    const value = approvalFieldValues[fieldId]?.value ?? '';
    return (value === CUSTOM_OPTION ? approvalFieldCustomValues[fieldId] ?? '' : value).trim();
  };

  // P21: "update" pushes to the Bill-To (and Ship-To); "create" makes a customer.
  const isUpdatingP21 = !isEclipse && customerAction === 'update';
  const isCreatingP21 = !isEclipse && customerAction === 'create';
  // Eclipse: a Bill-To step and a Ship-To step — the template's, else "update" updates both
  // (Reactivate) and "create" makes a Ship-To under the Bill-To. The Bill-To number is always
  // needed; the Ship-To number when an existing Ship-To is updated. Terms default to Net 30.
  const isCustomerActionFixed = !!(billToAction || shipToAction);
  const eclipseAction = isEclipse && customerAction !== 'none' ? customerAction : null;
  const eclipseBillToAction = billToAction ?? (eclipseAction === 'update' ? 'UPDATE' : 'NONE');
  const eclipseShipToAction = shipToAction ?? (eclipseAction === 'create' ? 'CREATE' : 'UPDATE');
  // The template's fixed action, e.g. "Update Bill-To and create Ship-To in Eclipse".
  const fixedEclipseActionLabel = `${[
    billToAction === 'UPDATE' ? 'Update Bill-To' : '',
    shipToAction === 'CREATE'
      ? 'Create Ship-To'
      : shipToAction === 'UPDATE'
      ? 'Update Ship-To'
      : '',
  ]
    .filter(Boolean)
    .join(' and ')} in Eclipse`;

  const handleApprove = () => {
    const approvedAmount = parseFloat(approvedAmountText.replace(/,/g, ''));
    const showsAmount = hasRequestedAmount && customerAction !== 'none';
    const nextErrors = {
      ...(isUpdatingP21
        ? {
            billTo: billToCustomerId.trim() ? '' : 'Bill-To customer number is required',
            shipTo:
              isShipToRequired && !shipToAccountNumber.trim()
                ? 'This form changes a Ship-To, so its account number is required'
                : '',
          }
        : {}),
      ...(eclipseAction
        ? {
            billTo: billToCustomerId.trim() ? '' : 'Bill-To customer number is required',
            shipTo:
              eclipseShipToAction === 'UPDATE' && !shipToAccountNumber.trim()
                ? 'Ship-To number is required'
                : '',
          }
        : {}),
      amount:
        showsAmount && (!approvedAmountText.trim() || isNaN(approvedAmount) || approvedAmount < 0)
          ? 'Enter the approved amount'
          : '',
    };
    const nextApprovalFieldErrors = Object.fromEntries(
      approvalFields
        .filter((field) => field.required && !resolveApprovalFieldValue(field.id))
        .map((field) => [field.id, `${field.label} is required`])
    );
    setErrors(nextErrors);
    setApprovalFieldErrors(nextApprovalFieldErrors);
    if (
      nextErrors.billTo ||
      nextErrors.shipTo ||
      nextErrors.amount ||
      Object.keys(nextApprovalFieldErrors).length > 0
    ) {
      return;
    }
    onConfirm({
      customerAction,
      billToCustomerId: billToCustomerId.trim(),
      shipToAccountNumber: shipToAccountNumber.trim(),
      notifyEmails,
      message: message.trim(),
      createAsCod: isCreatingP21 && createAsCod,
      ...(eclipseAction && terms ? { terms } : {}),
      ...(showsAmount ? { approvedCreditLimit: approvedAmount } : {}),
      approvalAdditionalFieldValues: approvalFields
        .map((field) => ({
          id: field.id,
          name: field.label,
          erpId: approvalFieldValues[field.id]?.erpId || field.erpId,
          value: resolveApprovalFieldValue(field.id),
        }))
        .filter((field) => !!field.value),
    });
  };

  return (
    <DillModal
      open={open}
      onClose={() => !isApproving && handleClose()}
      title="Approve Internal Form"
      width="640px"
      maxWidth="640px"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleClose}
            isDisabled={isApproving}
          />
          <DillButton
            label={
              eclipseAction === 'create'
                ? 'Approve & Create Ship-To'
                : eclipseAction === 'update'
                ? 'Approve & Update Eclipse'
                : isUpdatingP21
                ? 'Approve & Update P21'
                : isCreatingP21
                ? 'Approve & Create in P21'
                : 'Approve'
            }
            onClick={handleApprove}
            isLoading={isApproving}
            isDisabled={isApproving}
          />
        </div>
      }>
      <div className="flex flex-col gap-4">
        {isErpUpdateDisabled ? (
          <p className="text-sm GREY_600-CLR">
            This form&apos;s template is set to never update {integrationName}, so approving
            won&apos;t change anything in {integrationName}.
          </p>
        ) : (
          <DillDropdown
            containerClassName="h-[40px] p-1"
            label="Customer action on approval"
            value={customerAction}
            items={[
              {
                value: 'update',
                label: isEclipse
                  ? isCustomerActionFixed
                    ? fixedEclipseActionLabel
                    : 'Update Bill-To and Ship-To in Eclipse'
                  : `Update customer in ${integrationName}`,
              },
              {
                value: 'create',
                label: isEclipse
                  ? isCustomerActionFixed
                    ? fixedEclipseActionLabel
                    : 'Create Ship-To in Eclipse'
                  : `Create customer in ${integrationName}`,
              },
              { value: 'none', label: `Don't update ${integrationName}` },
            ].filter(
              (item) =>
                !isCustomerActionFixed ||
                item.value === 'none' ||
                item.value === defaultCustomerAction
            )}
            onSelectChange={(item) => {
              setCustomerAction(item.value as InternalFormCustomerAction);
              setErrors({});
            }}
          />
        )}
        {eclipseAction && (
          <EclipseFormApprovalFields
            open={open}
            billToAction={eclipseBillToAction}
            shipToAction={eclipseShipToAction}
            billToCustomerId={billToCustomerId}
            billToError={errors.billTo || ''}
            onBillToChange={(value) => {
              setBillToCustomerId(value);
              setErrors((prev) => ({ ...prev, billTo: '' }));
            }}
            shipToAccountNumber={shipToAccountNumber}
            shipToError={errors.shipTo || ''}
            onShipToChange={(value) => {
              setShipToAccountNumber(value);
              setErrors((prev) => ({ ...prev, shipTo: '' }));
            }}
            terms={terms}
            onTermsChange={setTerms}
          />
        )}
        {isCreatingP21 && (
          <>
            <p className="text-sm GREY_600-CLR">
              Approving creates a new P21 customer from this form&apos;s name, addresses, branch,
              sales rep and Marketing Segment. Its new account number is saved on the form.
            </p>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={createAsCod}
                onChange={(e) => setCreateAsCod(e.target.checked)}
              />
              Create as a COD account (COD credit status and terms)
            </label>
          </>
        )}
        {isUpdatingP21 && (
          <>
            <p className="text-sm GREY_600-CLR">
              Confirm the P21 account numbers. Approving pushes this form&apos;s changes to these
              accounts.
            </p>
            <AppInputField
              id="approveBillToCustomerId"
              label="Customer Number in P21 (Bill-To)"
              isRequired
              value={billToCustomerId}
              errorText={errors.billTo || ''}
              onTextChange={(text: string) => {
                setBillToCustomerId(text);
                setErrors((prev) => ({ ...prev, billTo: '' }));
              }}
            />
            <AppInputField
              id="approveShipToAccountNumber"
              label="Ship-To Account Number"
              isRequired={isShipToRequired}
              placeholder={isShipToRequired ? '' : 'Only needed when a Ship-To is changing'}
              value={shipToAccountNumber}
              errorText={errors.shipTo || ''}
              onTextChange={(text: string) => {
                setShipToAccountNumber(text);
                setErrors((prev) => ({ ...prev, shipTo: '' }));
              }}
            />
          </>
        )}
        {hasRequestedAmount && customerAction !== 'none' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
            <div className="rounded-lg bg-gray-50 px-4 py-3 flex flex-col">
              <span className="text-xs text-gray-500">Amount Requested</span>
              <span className="text-base font-semibold text-gray-900">
                {`$${(requestedCreditLimit ?? 0).toLocaleString('en-US')}`}
              </span>
            </div>
            <AppInputField
              id="approveAmountApproved"
              label="Amount Approved"
              isRequired
              prefix="$"
              value={approvedAmountText}
              errorText={errors.amount || ''}
              onTextChange={(text: string) => {
                // Digits and one decimal point, with commas in the whole-number part.
                const digitsOnly = text.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                const [whole, decimals] = digitsOnly.split('.');
                const wholeDigits = whole.replace(/^0+(?=\d)/, '');
                setApprovedAmountText(
                  (wholeDigits ? Number(wholeDigits).toLocaleString('en-US') : '') +
                    (decimals !== undefined ? `.${decimals.slice(0, 2)}` : '')
                );
                setErrors((prev) => ({ ...prev, amount: '' }));
              }}
            />
          </div>
        )}
        {approvalFields.length > 0 && (
          <ApprovalAdditionalFieldInputs
            fields={approvalFields}
            values={approvalFieldValues}
            customValues={approvalFieldCustomValues}
            errors={approvalFieldErrors}
            onChange={(fieldId, value, erpId) => {
              setApprovalFieldValues((prev) => ({ ...prev, [fieldId]: { value, erpId } }));
              setApprovalFieldErrors((prev) => ({ ...prev, [fieldId]: '' }));
            }}
            onCustomChange={(fieldId, value) => {
              setApprovalFieldCustomValues((prev) => ({ ...prev, [fieldId]: value }));
              setApprovalFieldErrors((prev) => ({ ...prev, [fieldId]: '' }));
            }}
          />
        )}
        <EmailTagList
          label="Emails to notify on approval"
          emails={notifyEmails}
          onChange={setNotifyEmails}
        />
        <AppInputField
          label="Additional Message (Optional)"
          inputType="textarea"
          placeholder="Enter any additional message"
          value={message}
          onTextChange={(text: string) => setMessage(text)}
        />
      </div>
    </DillModal>
  );
};

export default ApproveInternalFormModal;
